import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button, Card } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { m } from '@/paraglide/messages.js';

/**
 * What Wundo does, said once and in full, before anything is asked for.
 *
 * Five things rather than a tour: somebody about to hand over their calendar is
 * owed a straight answer to "what is this for", and a screen that saves that
 * answer for later is a screen that asks on trust it has not earned. Nothing is
 * stored here either — it reads, and it moves on.
 */
export const Route = createFileRoute('/onboarding/what-you-get')({
  component: OnboardingWhatYouGetPage,
});

interface Offering {
  title: () => string;
  description: () => string;
  /** Whether it is the notetaker's doing, and so gone without a calendar. */
  needsCalendar?: boolean;
}

const OFFERINGS: Offering[] = [
  {
    title: m.onboarding_value_prep_title,
    description: m.onboarding_value_prep_desc,
  },
  {
    title: m.onboarding_value_practice_title,
    description: m.onboarding_value_practice_desc,
  },
  {
    title: m.onboarding_value_during_title,
    description: m.onboarding_value_during_desc,
    needsCalendar: true,
  },
  {
    title: m.onboarding_value_report_title,
    description: m.onboarding_value_report_desc,
    needsCalendar: true,
  },
  {
    title: m.onboarding_value_memory_title,
    description: m.onboarding_value_memory_desc,
  },
];

export function OnboardingWhatYouGetPage() {
  const navigate = useNavigate();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /*
   * An organisation without the calendar integration has no notetaker either,
   * so two of the five would be promises Wundo cannot keep here — and the step
   * after this one is the conversation typed in by hand rather than the ask.
   */
  const calendar = user?.calendar_enabled !== false;
  const offerings = OFFERINGS.filter(
    (offering) => calendar || offering.needsCalendar !== true,
  );

  return (
    <Card
      padding='none'
      className='mx-auto w-full max-w-150 px-11 pt-11 pb-9 max-mobile:px-6 max-mobile:pt-8'
    >
      <p className='mb-3.5 text-xs font-bold tracking-wider text-ink-muted uppercase'>
        {m.onboarding_value_kicker()}
      </p>
      <h1 className='mb-6 text-xl font-medium tracking-tight'>
        {m.onboarding_value_title()}
      </h1>

      <ul className='flex flex-col gap-5'>
        {offerings.map((offering) => (
          <li key={offering.title()} className='flex gap-3.5'>
            {/* A dot, not a tick: nothing here is done yet, it is what is coming. */}
            <span
              aria-hidden='true'
              className='mt-2 size-2 shrink-0 rounded-full bg-accent'
            />
            <div className='min-w-0'>
              <p className='text-base font-bold text-ink'>{offering.title()}</p>
              <p className='text-sm text-ink-muted'>{offering.description()}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className='mt-8 flex items-center gap-3'>
        <Button
          variant='accent'
          shape='block'
          size='lg'
          onClick={() =>
            void navigate({
              to: calendar ? '/onboarding/calendar' : '/onboarding/first-meeting',
            })
          }
        >
          {m.onboarding_value_continue()}
          <ArrowRight aria-hidden='true' className='size-4' />
        </Button>
        <Button
          variant='ghost'
          shape='block'
          size='lg'
          onClick={() => void navigate({ to: '/onboarding/welcome' })}
        >
          {m.onboarding_back()}
        </Button>
      </div>
    </Card>
  );
}
